import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// Material UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import QrCodeIcon from "@mui/icons-material/QrCode";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";

// Components
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";

// Services
import {
  getWhatsAppQRCode,
  getWhatsAppStatus,
} from "../../../api/DeliveryCenter/whatsapp";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";

const POLL_INTERVAL = 3000;
const QR_TIMEOUT = 60000;
const QR_RETRY_INTERVAL = 4000;
const QR_MAX_RETRIES = 5;

export default function ConnectWhatsAppModal({
  show,
  closeModal,
  instance,
  onConnected,
}) {
  const theme = useTheme();

  const [qrCodeBase64, setQrCodeBase64] = useState("");
  const [connected, setConnected] = useState(false);
  const [qrExpired, setQrExpired] = useState(false);
  const [qrFailed, setQrFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshingQr, setRefreshingQr] = useState(false);

  const pollRef = useRef(null);
  const qrTimerRef = useRef(null);
  const retryRef = useRef(null);
  const retryCountRef = useRef(0);

  const cleanup = useCallback(() => {
    clearInterval(pollRef.current);
    clearTimeout(qrTimerRef.current);
    clearTimeout(retryRef.current);
    retryCountRef.current = 0;
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await getWhatsAppStatus(instance.id);
        if (data.status === "connected") {
          cleanup();
          setConnected(true);

          dispatchSnackBarMessage(
            `whatsapp-connected-${new Date()}`,
            "WhatsApp conectado com sucesso!",
            "success"
          );

          setTimeout(() => {
            onConnected();
          }, 2000);
        }
      } catch (_) {}
    }, POLL_INTERVAL);
  }, [instance, onConnected, cleanup]);

  const startQrTimer = useCallback(() => {
    clearTimeout(qrTimerRef.current);
    setQrExpired(false);
    qrTimerRef.current = setTimeout(() => {
      setQrExpired(true);
      clearInterval(pollRef.current);
    }, QR_TIMEOUT);
  }, []);

  const fetchQrCode = useCallback(async () => {
    try {
      const { data } = await getWhatsAppQRCode(instance.id);
      if (data.qr_code_base64) {
        retryCountRef.current = 0;
        setQrFailed(false);
        setQrCodeBase64(data.qr_code_base64);
        startQrTimer();
        startPolling();
      } else if (retryCountRef.current < QR_MAX_RETRIES) {
        retryCountRef.current += 1;
        retryRef.current = setTimeout(() => fetchQrCode(), QR_RETRY_INTERVAL);
      } else {
        setQrFailed(true);
      }
    } catch (e) {
      if (e.response?.data?.already_connected) {
        setConnected(true);
        dispatchSnackBarMessage(
          `whatsapp-already-connected-${new Date()}`,
          "Esta instancia ja esta conectada.",
          "info"
        );
        setTimeout(() => onConnected(), 1500);
      } else if (retryCountRef.current < QR_MAX_RETRIES) {
        retryCountRef.current += 1;
        retryRef.current = setTimeout(() => fetchQrCode(), QR_RETRY_INTERVAL);
      } else {
        setQrFailed(true);
      }
    }
  }, [instance, startQrTimer, startPolling, onConnected]);

  useEffect(() => {
    if (show && instance) {
      setLoading(true);
      setQrCodeBase64("");
      setConnected(false);
      setQrExpired(false);
      fetchQrCode().finally(() => setLoading(false));
    }
    return cleanup;
  }, [show, instance]);

  const handleRefreshQr = useCallback(async () => {
    setRefreshingQr(true);
    setQrFailed(false);
    retryCountRef.current = 0;
    await fetchQrCode();
    setRefreshingQr(false);
  }, [fetchQrCode]);

  const handleClose = useCallback(() => {
    cleanup();
    closeModal();
  }, [cleanup, closeModal]);

  if (!instance) return null;

  return (
    <AnimatedModal
      isOpen={show}
      onClose={handleClose}
      width="90%"
      maxWidth="480px"
      zIndex={10000}
    >
      <ModalHeader
        title={`Conectar ${instance.instance_name}`}
        icon={QrCodeIcon}
        onClose={handleClose}
      />
      <ModalContent>
        <Box sx={{ px: 3, py: 2 }}>
          {connected ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CheckCircleIcon
                  sx={{ fontSize: 72, color: "#25D366", mb: 2 }}
                />
                <Typography
                  variant="h6"
                  fontWeight={600}
                  align="center"
                  gutterBottom
                >
                  WhatsApp Conectado!
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Sua instancia foi conectada com sucesso.
                </Typography>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mb: 3 }}
                >
                  Abra o WhatsApp no celular, acesse{" "}
                  <strong>Dispositivos Conectados</strong> e escaneie o QR Code
                  abaixo.
                </Typography>

                {loading ? (
                  <Box
                    sx={{
                      width: 280,
                      height: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress sx={{ color: "#25D366" }} />
                  </Box>
                ) : qrExpired ? (
                  <Box
                    sx={{
                      width: 280,
                      height: 280,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `2px dashed ${alpha(
                        theme.palette.warning.main,
                        0.5
                      )}`,
                      borderRadius: 3,
                      mb: 2,
                    }}
                  >
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                      QR Code expirado
                    </Alert>
                    <Button
                      variant="outlined"
                      startIcon={
                        refreshingQr ? (
                          <CircularProgress size={18} />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      onClick={handleRefreshQr}
                      disabled={refreshingQr}
                      sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                      Atualizar QR Code
                    </Button>
                  </Box>
                ) : qrCodeBase64 ? (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      backgroundColor: "#fff",
                      mb: 2,
                    }}
                  >
                    <img
                      src={
                        qrCodeBase64.startsWith("data:")
                          ? qrCodeBase64
                          : `data:image/png;base64,${qrCodeBase64}`
                      }
                      alt="QR Code WhatsApp"
                      style={{ width: 260, height: 260 }}
                    />
                  </Box>
                ) : qrFailed ? (
                  <Box
                    sx={{
                      width: 280,
                      height: 280,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `2px dashed ${alpha(
                        theme.palette.error.main,
                        0.4
                      )}`,
                      borderRadius: 3,
                      mb: 2,
                    }}
                  >
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      Nao foi possivel obter o QR Code
                    </Alert>
                    <Button
                      variant="outlined"
                      startIcon={
                        refreshingQr ? (
                          <CircularProgress size={18} />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      onClick={handleRefreshQr}
                      disabled={refreshingQr}
                      sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                      Tentar novamente
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 280,
                      height: 280,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress sx={{ color: "#25D366", mb: 2 }} />
                    <Typography variant="caption" color="text.secondary">
                      Obtendo QR Code...
                    </Typography>
                  </Box>
                )}

                {!loading && qrCodeBase64 && !qrExpired && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                  >
                    Aguardando conexao...
                  </Typography>
                )}
              </Box>
            </motion.div>
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
